import React, { useEffect, useMemo, useState } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import Notification from "../../components/Common/Notification";
import ConfirmationDialog from "../../components/Common/ConfirmationDialog";
import functions from "../../util/functions";
import ajaxConfig from "../../util/remote/ajaxConfig";
import { formatDate } from "../../util/araknerdlib";


const safe = (v, fb = "—") => (v === null || v === undefined || v === "" ? fb : v);

const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const CompanyResetPage = () => {
  const [info, setInfo] = useState(null);

  const [backups, setBackups] = useState(null); // null = loading
  const [loadingBackups, setLoadingBackups] = useState(false);

  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const [busyReset, setBusyReset] = useState(false);

  const [restoreTarget, setRestoreTarget] = useState(null);
  const [busyRestoreId, setBusyRestoreId] = useState(null);

  const canReset = ack1 && ack2 && String(confirmText).trim().toUpperCase() === "RESET";

  const loadBackups = async () => {
    setLoadingBackups(true);
    const server_response = await ajaxConfig.fetchBackupList();
    setLoadingBackups(false);
    if(server_response.status === "OK"){
      setBackups(server_response.details);
      return;
    }else{
        setBackups("404");
    }
  };

  useEffect(() => {
    loadBackups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizedBackups = useMemo(() => {
    if (!Array.isArray(backups)) return [];
    // newest first
    const out = [...backups].map((b) => ({
      id: b.id,
      title: b.title ?? b.backup_name ?? "Auto backup",
      reason: b.reason ?? b.trigger ?? "Reset profile",
      created_at: b.created_at ?? b.created_on ?? b.date,
      created_by: b.created_by?.username ?? b.created_by ?? "",
      size: b.size ?? b.file_size ?? null,
      records: b.records ?? b.record_count ?? null,
      period: b.period ?? b.financial_period ?? null,
      notes: b.notes ?? b.comment ?? "",
      raw: b,
    }));
    out.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    return out;
  }, [backups]);

  const handleReset = async () => {
    try {
      setInfo(
        <Notification
          type="info"
          message="Resetting profile... creating backup and deleting transactions."
          closable={false}
          iconClass="fa fa-info-circle"
          loader={true}
        />
      );

      setBusyReset(true);
      const server_response = await ajaxConfig.resetProfile({reason: "User initiated via reset page", notes: "Reset confirmed by user."});
      if (server_response.status === "OK") {
        setInfo(<Notification type="success" message={server_response?.message || "Profile reset successful."} closable={false} />);
        // reset UI state
        setAck1(false);
        setAck2(false);
        setConfirmText("");
        // refresh backups
        await loadBackups();
      } else {
        setInfo(
          <Notification
            type="warning"
            message={server_response?.message || "Reset failed. Please try again."}
            closable={false}
            iconClass="fa fa-info-circle"
          />
        );
      }
    } catch (e) {
      setInfo(
        <Notification
          type="danger"
          message="Reset failed due to a network/server error."
          closable={false}
          iconClass="fa fa-info-circle"
        />
      );
    } finally {
      setBusyReset(false);
      setShowResetConfirm(false);
    }
  };

  const handleRestore = async (backup) => {
    if (!backup?.id) return;

    try {
      setBusyRestoreId(backup.id);
      setInfo(
        <Notification
          type="info"
          message={`Restoring from backup: ${backup.title}...`}
          closable={false}
          iconClass="fa fa-info-circle"
          loader={true}
        />
      );

      const server_response = await ajaxConfig.restoreBackup(backup.id);
      if (server_response?.status === "OK") {
        setInfo(<Notification type="success" message={server_response?.message || "Restore started."} closable={false} />);
        setRestoreTarget(null);
      } else {
        setInfo(
          <Notification
            type="warning"
            message={server_response?.message || "Restore failed. Please try again."}
            closable={false}
            iconClass="fa fa-info-circle"
          />
        );
      }
    } catch (e) {
      setInfo(
        <Notification
          type="danger"
          message="Restore failed due to a network/server error."
          closable={false}
          iconClass="fa fa-info-circle"
        />
      );
    } finally {
      setBusyRestoreId(null);
    }
  };


  return (<>
    <PageContainer>
      <MainContent>

        <PageTitle
          title="Reset Profile"
          description="Delete all transaction data and start afresh."
          iconClass="fa fa-cogs"
          sticky={true}
        />

        {info}

        <div className="row">
          {/* LEFT: Danger Zone */}
          <div className="col-lg-5 mb-3">
            <div className="yx-card yx-resetCard">
              <div className="yx-card-head">
                <div className="d-flex align-items-start justify-content-between" style={{ gap: 12 }}>
                  <div>
                    <div className="yx-kicker">Danger zone</div>
                    <div className="yx-title" style={{ marginTop: 2 }}>
                      Reset accounting data
                    </div>
                    <div className="yx-sub" style={{ marginTop: 6 }}>
                      This will remove <strong>all</strong> accounting transactions. The system will automatically create a backup first
                      so you can restore later.
                    </div>
                  </div>

                  <span className="yx-pill yx-pill--muted">
                    <i className="fa fa-shield-alt" />
                    Backup first
                  </span>
                </div>
              </div>

              <div className="yx-card-body">
                <div className="yx-resetList">
                  <div className="yx-resetItem">
                    <span className="yx-resetDot" />
                    <div>
                      <div className="yx-row">Deletes all ledgers & journals</div>
                      <div className="yx-muted" style={{ fontSize: 12.5 }}>
                        Journal entries, invoices, payments, checking records, etc.
                      </div>
                    </div>
                  </div>

                  <div className="yx-resetItem">
                    <span className="yx-resetDot" />
                    <div>
                      <div className="yx-row">Keeps configuration</div>
                      <div className="yx-muted" style={{ fontSize: 12.5 }}>
                        Company profile, chart of accounts, users and settings remain intact (unless you decide otherwise).
                      </div>
                    </div>
                  </div>

                  <div className="yx-resetItem">
                    <span className="yx-resetDot" />
                    <div>
                      <div className="yx-row">Creates an automatic backup</div>
                      <div className="yx-muted" style={{ fontSize: 12.5 }}>
                        The backup appears in “Recent backups” on the right.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="yx-divider" />

                <label className="yx-resetAck">
                  <input type="checkbox" checked={ack1} onChange={(e) => setAck1(e.target.checked)} />
                  <span>I understand this will delete all accounting transactions.</span>
                </label>

                <label className="yx-resetAck">
                  <input type="checkbox" checked={ack2} onChange={(e) => setAck2(e.target.checked)} />
                  <span>I understand I can restore using a backup later.</span>
                </label>

                <div className="yx-resetConfirm">
                  <div className="yx-muted" style={{ fontSize: 12.5, fontWeight: 800 }}>
                    Type <span className="yx-resetCode">RESET</span> to enable the reset button:
                  </div>

                  <input
                    className="form-control yx-resetInput"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type RESET"
                    spellCheck={false}
                  />
                </div>

                <button
                  type="button"
                  className={`yx-resetBtn ${canReset ? "" : "is-disabled"}`}
                  disabled={!canReset || busyReset}
                  onClick={() => setShowResetConfirm(true)}
                >
                  <i className={`fa ${busyReset ? "fa-spinner fa-spin" : "fa-trash-alt"}`} />
                  {busyReset ? "Resetting..." : "Reset profile"}
                </button>

                <div className="yx-muted" style={{ fontSize: 12, marginTop: 10 }}>
                  Tip: If you’re just testing, consider doing this after exporting reports you may need.
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Backups */}
          <div className="col-lg-7 mb-3">
            <div className="yx-card yx-backupCard">
              <div className="yx-card-head">
                <div className="d-flex align-items-center justify-content-between" style={{ gap: 12 }}>
                  <div>
                    <div className="yx-kicker">Safety</div>
                    <div className="yx-title" style={{ marginTop: 2 }}>
                      Recent backups
                    </div>
                    <div className="yx-sub" style={{ marginTop: 6 }}>
                      Restore your accounting data from an auto-backup created during reset.
                    </div>
                  </div>

                  <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <span className="yx-pill yx-pill--muted" title="Number of backups">
                      <i className="fa fa-database" />
                      {loadingBackups ? "…" : normalizedBackups.length}
                    </span>

                    <button className="yx-btnSoft" type="button" onClick={loadBackups} disabled={loadingBackups}>
                      <i className={`fa ${loadingBackups ? "fa-sync fa-spin" : "fa-sync"}`} />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

              <div className="yx-card-body">
                {backups === "404" && (
                  <Notification type="info" message="No backups found." closable={false} />
                )}

                {!backups && (
                  <div className="text-center py-3 yx-muted">
                    <i className="fa fa-spinner fa-spin mr-2" />
                    Loading backups...
                  </div>
                )}

                {backups === "404" && (
                  <div className="yx-empty">
                    <div className="yx-emptyIcon">
                      <i className="fa fa-inbox" />
                    </div>
                    <div className="yx-row">No backups yet</div>
                    <div className="yx-muted" style={{ fontSize: 12.5 }}>
                      A backup will appear here automatically the first time you reset the profile.
                    </div>
                  </div>
                )}

                {Array.isArray(backups) && normalizedBackups.length > 0 && (
                  <div className="yx-backupList overflow-auto" style={{maxHeight: '430px'}}>
                    {normalizedBackups.slice(0, 10).map((b) => (
                      <div key={String(b.id)} className="yx-backupRow">
                        <div className="yx-backupLeft">
                          <div className="yx-backupTitle">
                            <i className="fa fa-box-archive" />
                            <span title={b.title}>{b.title}</span>
                          </div>

                          <div className="yx-backupMeta">
                            <span className="yx-backupChip">
                              <i className="fa fa-clock" />
                              {b.created_at ? functions.formatDate(b.created_at) : "—"}
                            </span>

                            {b.records != null && (
                              <span className="yx-backupChip">
                                <i className="fa fa-layer-group" />
                                {fmtMoney(b.records)} records
                              </span>
                            )}

                            {!!b.created_by && (
                              <span className="yx-backupChip">
                                <i className="fa fa-user" />
                                {b.created_by}
                              </span>
                            )}

                            {!!b.reason && (
                              <span className="yx-backupChip">
                                <i className="fa fa-tag" />
                                {b.reason}
                              </span>
                            )}
                          </div>

                          {!!b.notes && (
                            <div className="yx-muted" style={{ fontSize: 12.5, marginTop: 6 }}>
                              {b.notes}
                            </div>
                          )}
                        </div>

                        <div className="yx-backupRight">
                          <button
                            type="button"
                            className="yx-btnSoft"
                            onClick={() => setRestoreTarget(b)}
                            disabled={busyRestoreId === b.id}
                            title="Restore this backup"
                          >
                            <i className="fa fa-rotate-left" />
                            Restore
                          </button>

                          <div className="yx-muted" style={{ fontSize: 12, textAlign: "right" }}>
                            #: {safe(b.id)}
                          </div>
                        </div>
                      </div>
                    ))}

                    {normalizedBackups.length > 10 && (
                      <div className="yx-muted" style={{ fontSize: 12.5, marginTop: 10 }}>
                        Showing 10 most recent backups.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        
      </MainContent>
    </PageContainer>
    {/* Confirm RESET */}
        <ConfirmationDialog
          show={showResetConfirm}
          title="Confirm profile reset"
          alertDescription="This will permanently delete all accounting transaction records but not your configuration settings."
          notes="A backup will be created automatically before the deletion. You can restore it from the Recent Backups list."
          buttonLabel={busyReset ? "Resetting..." : "Yes, reset profile"}
          handleClose={() => setShowResetConfirm(false)}
          handleConfirm={handleReset}
          themeClass="danger"
        />

        {/* Confirm RESTORE */}
        <ConfirmationDialog
          show={!!restoreTarget}
          title="Restore backup"
          alertDescription={restoreTarget? `Restore data from backup: ${restoreTarget?.title || "Backup"} - ${formatDate(restoreTarget?.created_at)}` : ""}
          notes="This will re-import the backed up data into your profile. Proceed carefully if you already have new entries. All data after the backup date will be lost."
          buttonLabel={busyRestoreId ? "Restoring..." : "Restore backup"}
          handleClose={() => setRestoreTarget(null)}
          handleConfirm={() => handleRestore(restoreTarget)}
          themeClass="primary"
        />
    </>
  );
};


export default CompanyResetPage;
